import { LICENSE } from '../../../constants/reading/CommonConstants';
import { APP_LANGUAGE } from '../../../constants/shared/CommonConstants';
import {
    FETCH_ALL_RESULTS,
    FETCH_ALL_ADDITIONAL_RESULTS,
    FETCH_D8_RESULTS,
    FETCH_SHARED_RESULTS,
    INVALIDATE_BASIC_COMPREHENSION_RESULT,
} from '../../actionTypes/shared/apiActionTypes';
import TRCClient from '../../clients/shared/TRCClient';
import VocaboClient from '../../clients/shared/VocaboClient';
import SpellingClient from '../../clients/shared/SpellingClient';
import OLClient from '../../clients/shared/OLClient';
import SkylightClient from '../../clients/reading/SkylightClient';
import TROClient from '../../clients/reading/TROClient';
import DecodingClient from '../../clients/reading/DecodingClient';
import RANClient from '../../clients/reading/RANClient';
import TranslatorClient from '../../clients/reading/TranslatorClient';
import SharedTranslatorClient from '../../clients/shared/TranslatorClient';
import {
    FETCH_READING_RESULTS,
    FETCH_TRO_AUDIO_DATA,
    FETCH_BURST_RESULTS_FOR_YEAR,
    INVALIDATE_D8_RESULT,
    FETCH_D8_RESULT_BY_ID,
    REMOVE_D8_RESULT_AFTER_INVALIDATION,
    INVALIDATE_D8_SECOND_ATTEMPT_RESULTS,
} from '../../actionTypes/reading/apiActionTypes';
import { PARAM } from '../../../constants/reading/ApiConstants';
import { URL } from '../../../constants/shared/ApiConstants';
import { apiActionCreator, multyApiCallActionCreator } from '../../actionCreators';
import {
    getDemoClassFlag,
    isMuniOrDistrictGroupUser,
} from '../../../utils/shared/resultReducerHelper';
import { selectors } from '../../../root/reading/root';
import DecodeClient from '../../clients/reading/DecodeClient';
import KeycloakClient from '../../clients/shared/KeycloakClient';
import D8Client from '../../clients/reading/D8Client';
import { MEASURE } from '../../../constants/shared/MeasuresConstants';
import MAZEClient from '../../clients/reading/MAZEClient';

const { LECTURA_RESULTS_URL } = URL;

const { D8, TRC, VOCABO, SPELLING, RAN, OL, BURST, TRC_ESPANOL, VOCABO_ESPANOL, EXPRESS } = LICENSE;

export const getAdditionalApiParams = ({ districtId, schoolId, classId, isDemoClass }) => ({
    ...getDemoClassFlag(isDemoClass, classId),
    [PARAM.INST_CONTEXT]: isMuniOrDistrictGroupUser(schoolId) ? districtId : schoolId,
});

export const fetchStudentResults = () => async (dispatch, getState) => {
    const state = getState();
    const {
        studentIds,
        districtId,
        schoolId,
        classId,
        licensingInfo,
        isDemoClass,
        year: schoolYear,
    } = selectors.getCommonData(state);
    const isMasUser = KeycloakClient.isMasUser();
    const fetchStudentResultsCallsConfig = {
        [FETCH_SHARED_RESULTS.D8]: () =>
            licensingInfo.includes(D8) &&
            TranslatorClient.getResults(
                studentIds,
                districtId,
                getAdditionalApiParams(selectors.getCommonData(state)),
            ),
        [FETCH_SHARED_RESULTS.R3D]: () =>
            licensingInfo.includes(D8) &&
            (licensingInfo.includes(TRC) || licensingInfo.includes(BURST)) &&
            SkylightClient.getR3DResults(studentIds, districtId, schoolId, classId),
        // TODO - Update the condition once the license is available
        [FETCH_SHARED_RESULTS.DECODE]: () =>
            licensingInfo.includes(D8) &&
            !isMasUser &&
            DecodeClient.getResults(studentIds, schoolYear),
        [FETCH_SHARED_RESULTS.TRC_ENG]: () =>
            licensingInfo.includes(TRC) &&
            TRCClient.getResults(
                studentIds,
                APP_LANGUAGE.ENGLISH,
                getDemoClassFlag(isDemoClass, classId),
            ),
        [FETCH_READING_RESULTS.TRO]: () =>
            licensingInfo.includes(EXPRESS) &&
            TROClient.getResults(studentIds, {
                [PARAM.INST_UID]: isMuniOrDistrictGroupUser(schoolId) ? districtId : schoolId,
                [PARAM.WITH_ALL_RESULTS]: true,
            }),
        [FETCH_SHARED_RESULTS.VOCABO_ENG]: () =>
            licensingInfo.includes(VOCABO) &&
            VocaboClient.getResults(studentIds, APP_LANGUAGE.ENGLISH),
        [FETCH_SHARED_RESULTS.SPELLING]: () =>
            licensingInfo.includes(SPELLING) &&
            SpellingClient.getResults(studentIds, APP_LANGUAGE.ENGLISH),
        [FETCH_READING_RESULTS.RAN]: () =>
            licensingInfo.includes(RAN) && RANClient.getResults(studentIds),
        [FETCH_READING_RESULTS.DECODING]: () =>
            licensingInfo.includes(EXPRESS) && DecodingClient.getResults(studentIds),
        [FETCH_SHARED_RESULTS.OL_ENG]: () =>
            licensingInfo.includes(OL) &&
            OLClient.getResults(
                studentIds,
                APP_LANGUAGE.ENGLISH,
                getDemoClassFlag(isDemoClass, classId),
            ),
    };
    await multyApiCallActionCreator(dispatch, fetchStudentResultsCallsConfig, FETCH_ALL_RESULTS);
};

export const fetchAdditionalStudentResults = () => async (dispatch, getState) => {
    const state = getState();
    const { studentIds, isDemoClass, classId } = selectors.getCommonData(state);
    const licensingInfo = selectors.getDualLanguageLicensingInfo(state);

    const fetchAdditionalStudentResultsCalls = {
        [FETCH_SHARED_RESULTS.LECTURA]: () =>
            licensingInfo.includes(LICENSE.LECTURA) &&
            SharedTranslatorClient.getResults(LECTURA_RESULTS_URL, studentIds),
        [FETCH_SHARED_RESULTS.TRC_SPA]: () =>
            licensingInfo.includes(TRC_ESPANOL) &&
            TRCClient.getResults(
                studentIds,
                APP_LANGUAGE.SPANISH,
                getDemoClassFlag(isDemoClass, classId),
            ),
        [FETCH_SHARED_RESULTS.VOCABO_SPA]: () =>
            licensingInfo.includes(VOCABO_ESPANOL) &&
            VocaboClient.getResults(studentIds, APP_LANGUAGE.SPANISH),
    };
    await multyApiCallActionCreator(
        dispatch,
        fetchAdditionalStudentResultsCalls,
        FETCH_ALL_ADDITIONAL_RESULTS,
    );
};

export const fetchD8Results = () => async (dispatch, getState) => {
    const state = getState();
    const { studentIds, districtId } = selectors.getCommonData(state);
    const apiActionConfigObject = {
        apiParams: [studentIds, districtId, getAdditionalApiParams(selectors.getCommonData(state))],
    };
    await apiActionCreator(
        dispatch,
        FETCH_D8_RESULTS,
        TranslatorClient.getResults,
        apiActionConfigObject,
    );
};
export const fetchTroAudioData = (studentId, resultId, bookId) => async dispatch => {
    const additionalApiParams = {
        apiParams: [resultId, bookId],
        additionalResponseParams: { studentId, resultId, bookId },
    };
    await apiActionCreator(
        dispatch,
        FETCH_TRO_AUDIO_DATA,
        TROClient.getAudioData,
        additionalApiParams,
    );
};

export const fetchBurstResultsForYear =
    (studentId, districtId, schoolId, classId, year, applicationSid) => async dispatch => {
        const additionalApiParams = {
            apiParams: [studentId, districtId, schoolId, classId, year, applicationSid],
        };
        await apiActionCreator(
            dispatch,
            FETCH_BURST_RESULTS_FOR_YEAR,
            SkylightClient.getBurstResultsForYear,
            additionalApiParams,
        );
    };

export const getD8ResultById = resultId => async dispatch => {
    const apiActionConfigObject = {
        apiParams: [resultId],
    };
    await apiActionCreator(
        dispatch,
        FETCH_D8_RESULT_BY_ID,
        D8Client.getResultById,
        apiActionConfigObject,
    );
};

export const invalidateD8Result = result => async (dispatch, getState) => {
    const getResultToInvalidateFromStore = () => selectors.getResultToInvalidate(getState());

    const apiActionConfigObject = {
        preApiCallCalback: async () => {
            await dispatch(getD8ResultById(result.resultId));
        },
        afterApiCallCalback: async () => {
            dispatch({ type: REMOVE_D8_RESULT_AFTER_INVALIDATION, response: null });
            await dispatch(fetchD8Results());
        },
        apiParams: [getResultToInvalidateFromStore],
        isNeededStartAction: false,
    };
    await apiActionCreator(
        dispatch,
        INVALIDATE_D8_RESULT,
        D8Client.invalidateResult,
        apiActionConfigObject,
    );
};

export const invalidateMazeOnlineResult = result => async dispatch => {
    const apiActionConfigObject = {
        preApiCallCalback: () => {
            dispatch({
                type: INVALIDATE_BASIC_COMPREHENSION_RESULT.START,
                studentId: result.studentId,
            });
        },
        afterApiCallCalback: async () => {
            await dispatch(fetchD8Results());
        },
        apiParams: [result.resultId],
        additionalResponseParams: {
            studentId: result.studentId,
            resultId: result.resultId,
            basicComprehensionMeasure: MEASURE.MAZE,
        },
        isNeededStartAction: false,
    };
    await apiActionCreator(
        dispatch,
        INVALIDATE_BASIC_COMPREHENSION_RESULT,
        MAZEClient.deleteResult,
        apiActionConfigObject,
    );
};

export const invalidateD8SecondAttemptResults = studentId => async (dispatch, getState) => {
    const apiActionConfigObject = {
        afterApiCallCalback: async () => {
            await dispatch(fetchD8Results());
        },
        apiParams: [selectors.getCurrentSchoolYear(getState()), studentId],
    };
    await apiActionCreator(
        dispatch,
        INVALIDATE_D8_SECOND_ATTEMPT_RESULTS,
        D8Client.invalidateSecondAttemptResults,
        apiActionConfigObject,
    );
};
